import { useEffect, useState, useContext, useCallback, useRef, useMemo } from "react";
import { Grid, Button } from "@mui/material";
import styles from "./styles.module.css";
import toast from "react-hot-toast";
import TableCard from "./TableCard";
import { useNavigate, useLocation } from "react-router-dom";
import TableOrdersDialog from "../../popups/TableOrdersPopup/TableOrdersDialog";
import TableChairDialog from "../../popups/TableChairDialog/TableChairDialog";
import SalesContext from "context/SalesContext";
import CartDetailsDialog from "../../popups/CartDetailsDialog/CartDetailsDialog";
import SkeletonLoading from "../../utils/Loading/SkeletonLoading";
import {
  ROUTES,
  SALE_STATUS,
  TABLE_SELECTION_MODE,
  MERGE_LABELS,
  EVENTS,
  TABLE_STATUS,
  KOT_TITLES,
  STORAGE_KEYS,
  ALL_TABLES,
  KOT_STATUS,
} from "const";
import { eventsEmitter } from "utils/events";
import AreaTableContext from "context/AreaTableContext";
import SearchContext from "context/SearchContext";
import ShopContext from "context/ShopContext";
import { TerminalContext } from "context/TerminalContext";
import KotManager from "helpers/KotManager";
import * as TerminalHelper from "helpers/TermialHelper";
import KOTInvoice from "components/utils/PrintInvoice/KOTInvoice";
import QuickSaleButtons from "../../utils/QuickSaleButtons/QuickSaleButtons";
import useWebInvoicePrint from "helpers/InvoiceHelper";

function TableDashboard() {
  const navigate = useNavigate();
  const { sales, activeSale, createNewSoftSale, handleChangeSaleTable, handleMergeSales, refreshSales } =
    useContext(SalesContext);
  const { searchTerm, setSearchTerm } = useContext(SearchContext);
  const { areas, tables: rawTables, isLoading } = useContext(AreaTableContext);
  const { selectedShop } = useContext(ShopContext);
  const { displayAwaitingDispatchOrders } = useContext(TerminalContext);

  const kotPrintRef = useRef(null);

  const [allTables, setAllTables] = useState([]);
  const [tables, setTables] = useState([]);
  const [activeArea, setActiveArea] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.TABLE_DASHBOARD_ACTIVE_AREA) === ALL_TABLES ? ALL_TABLES : null;
  });
  const [selectedSale, setSelectedSale] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [salesDialogOpen, setSalesDialogOpen] = useState(false);
  const [selectedTableForSales, setSelectedTableForSales] = useState(null);
  const [filteredSalesForDialog, setFilteredSalesForDialog] = useState(null);
  const [salesDialogTitle, setSalesDialogTitle] = useState(null);
  const [tableChairDialogOpen, setTableChairDialogOpen] = useState(false);
  const [selectedTableForChairs, setSelectedTableForChairs] = useState(null);
  const [saleToPrint, setSaleToPrint] = useState(null);

  const location = useLocation();

  // Selection Mode State (Change Table)
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [saleToChange, setSaleToChange] = useState(null);
  const [newSelectedTable, setNewSelectedTable] = useState(null);

  // Merge Mode State
  const [isMergeMode, setIsMergeMode] = useState(false);
  const [selectedSalesForMerge, setSelectedSalesForMerge] = useState([]);

  const [isTableChange, setIsTableChange] = useState(false);

  const sortedAreas = useMemo(() => {
    return [...areas].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [areas]);

  const firstNonEmptyAreaId = useMemo(() => {
    if (activeArea !== ALL_TABLES) return null;
    return sortedAreas.find((area) => tables.some((t) => t?.area?.id === area.id))?.id;
  }, [activeArea, sortedAreas, tables]);

  const lastNonEmptyAreaId = useMemo(() => {
    if (activeArea !== ALL_TABLES) return null;
    const nonEmptyAreas = sortedAreas.filter((area) => tables.some((t) => t?.area?.id === area.id));
    return nonEmptyAreas.length > 0 ? nonEmptyAreas[nonEmptyAreas.length - 1].id : null;
  }, [activeArea, sortedAreas, tables]);

  const findActiveSalesForTable = useCallback(
    (tableId) => {
      // Find all draft/active sales for this table
      const matchingSales = sales.filter((sale) => {
        const isDraftOrPrinted = sale.status === SALE_STATUS.DRAFT || sale.status === SALE_STATUS.PRINTED;
        const isAwaitingDispatch =
          displayAwaitingDispatchOrders &&
          sale.status === SALE_STATUS.PAID &&
          [KOT_STATUS.PENDING, KOT_STATUS.PREPARING, KOT_STATUS.READY].includes(sale.kot_status);

        return (
          (isDraftOrPrinted || isAwaitingDispatch) &&
          sale.status !== SALE_STATUS.VOID &&
          Number(sale?.table?.id) === Number(tableId)
        );
      });
      return matchingSales;
    },
    [sales, displayAwaitingDispatchOrders],
  );

  const liveTableForChairs = useMemo(() => {
    if (!selectedTableForChairs?.id) return null;
    return allTables.find((t) => Number(t.id) === Number(selectedTableForChairs.id)) || selectedTableForChairs;
  }, [allTables, selectedTableForChairs]);

  const activeSalesForChairDialog = useMemo(() => {
    if (!selectedTableForChairs?.id) return [];
    return findActiveSalesForTable(selectedTableForChairs.id);
  }, [selectedTableForChairs?.id, findActiveSalesForTable]);

  const liveTableForSales = useMemo(() => {
    if (!selectedTableForSales?.id) return null;
    return allTables.find((t) => Number(t.id) === Number(selectedTableForSales.id)) || selectedTableForSales;
  }, [allTables, selectedTableForSales]);

  useEffect(() => {
    if (!rawTables || rawTables.length === 0) return;

    const deriveStatus = (salesForTable) => {
      if (!salesForTable || salesForTable.length === 0) return TABLE_STATUS.AVAILABLE;

      // If any sale is not paid (active), return Not Paid (priority?)
      const hasActive = salesForTable.some((s) => (s.status || "").toLowerCase() === SALE_STATUS.PRINTED);
      if (hasActive) return TABLE_STATUS.NOT_PAID;

      return TABLE_STATUS.DINE_IN;
    };

    const enrichedTables = rawTables
      .map((table) => {
        const salesForTable = findActiveSalesForTable(table?.id);

        const itemsCount = salesForTable.reduce((acc, sale) => acc + (sale.items?.length || 0), 0);
        const amount = salesForTable.reduce((acc, sale) => acc + (sale.payable || 0), 0);

        return {
          ...table,
          status: deriveStatus(salesForTable),
          itemsCount,
          amount,
          sales: salesForTable,
        };
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    setAllTables(enrichedTables);

    if (sortedAreas.length > 0) {
      const savedArea = localStorage.getItem(STORAGE_KEYS.TABLE_DASHBOARD_ACTIVE_AREA);
      let targetArea = activeArea;

      if (savedArea === ALL_TABLES) {
        targetArea = ALL_TABLES;
      } else if (savedArea != null && sortedAreas.some((a) => String(a.id) === String(savedArea))) {
        targetArea = sortedAreas.find((a) => String(a.id) === String(savedArea)).id;
      } else if (!activeArea || (activeArea !== ALL_TABLES && !sortedAreas.some((a) => a.id === activeArea))) {
        targetArea = sortedAreas[0].id;
      }

      if (activeArea !== targetArea) {
        setActiveArea(targetArea);
        const filtered =
          targetArea === ALL_TABLES ? enrichedTables : enrichedTables.filter((table) => table?.area?.id === targetArea);
        setTables(filtered);
      }
    }
  }, [rawTables, sales, activeSale, areas, activeArea, findActiveSalesForTable, sortedAreas]);

  useEffect(() => {
    if (!activeArea) return;

    const filtered = allTables.filter((table) => {
      const matchesArea = activeArea === ALL_TABLES || table?.area?.id === activeArea;
      const matchesSearch =
        table?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(table?.table_number || "").includes(searchTerm);

      return matchesArea && matchesSearch;
    });

    setTables(filtered);
  }, [activeArea, allTables, searchTerm]);

  useEffect(() => {
    if (location.state?.mode === TABLE_SELECTION_MODE.CHANGE_TABLE && location.state?.sale) {
      setSaleToChange(location.state.sale);
      setIsSelectionMode(true);
      setNewSelectedTable(null);
    }

    if (location.state?.refresh) {
      refreshSales?.();
      navigate(location.pathname, { replace: true, state: { ...location.state, refresh: false } });
    }
  }, [location.state, location.pathname, refreshSales, navigate]);

  const handleTableClick = async (table) => {
    if (isSelectionMode) {
      if (table?.chairs && table?.chairs.length > 0) {
        setSelectedTableForChairs(table);
        setTableChairDialogOpen(true);
        return;
      }
      setNewSelectedTable(table);
      return;
    }

    if (isMergeMode) {
      const tableSales = table?.sales || [];
      if (tableSales.length === 0) {
        toast.error("No active orders on this table to merge.");
        return;
      }

      if (tableSales.length === 1) {
        handleToggleMergeSale(tableSales[0]);
        return;
      }

      // Multiple sales on table, show dialog to pick
      setSelectedTableForSales(table);
      setSalesDialogOpen(true);
      return;
    }

    const tableSales = table.sales || [];
    const hasChairs = Array.isArray(table?.chairs) && table?.chairs.length > 0;

    if (hasChairs) {
      setSelectedTableForChairs(table);
      setTableChairDialogOpen(true);
      return;
    }

    if (tableSales.length === 1) {
      setSelectedSale(tableSales[0]);
      setDialogOpen(true);
      setSearchTerm("");
      return;
    }

    if (tableSales.length > 1) {
      setSelectedTableForSales(table);
      setSalesDialogOpen(true);
      return;
    }

    // No sales, create new one
    handleStartNewOrder(table);
  };

  const handleSelectTableFromDialog = (table) => {
    if (isSelectionMode) {
      setNewSelectedTable({
        ...table,
        chair: null,
      });
      setTableChairDialogOpen(false);
      return;
    }
    setTableChairDialogOpen(false);
    handleStartNewOrder({
      ...table,
      chair: null,
    });
  };

  const handleSelectChairFromDialog = (chair) => {
    const table = liveTableForChairs;
    if (isSelectionMode) {
      setNewSelectedTable({
        ...table,
        chair: { id: chair.id, name: chair.name },
      });
      setTableChairDialogOpen(false);
      return;
    }
    setTableChairDialogOpen(false);
    handleStartNewOrder({
      ...table,
      chair: { id: chair.id, name: chair.name },
    });
  };

  const handleSelectSaleFromChairDialog = (sale) => {
    setSelectedSale(sale);
    setTableChairDialogOpen(false);
    setDialogOpen(true);
    setSearchTerm("");
  };

  const handleViewFilteredOrders = (salesSubset, title) => {
    setSelectedTableForSales(liveTableForChairs);
    setFilteredSalesForDialog(salesSubset);
    setSalesDialogTitle(title);
    setSalesDialogOpen(true);
  };

  const handleViewAllOrdersFromChairDialog = () => {
    setSelectedTableForSales(liveTableForChairs);
    setFilteredSalesForDialog(null);
    setSalesDialogTitle(null);
    setSalesDialogOpen(true);
  };

  const handleStartNewOrder = (table) => {
    try {
      createNewSoftSale({ table });
      setSearchTerm("");
      setSalesDialogOpen(false);
      navigate(ROUTES.SALES);
    } catch (err) {
      toast.error("Error creating new order");
    }
  };

  const handleCloseSalesDialog = () => {
    setSalesDialogOpen(false);
    setFilteredSalesForDialog(null);
    setSalesDialogTitle(null);
  };

  const handleNewOrderFromSalesDialog = () => {
    handleCloseSalesDialog();
    const table = liveTableForSales;
    if (table?.chairs && table?.chairs.length > 0) {
      setSelectedTableForChairs(table);
      setTableChairDialogOpen(true);
    } else {
      handleStartNewOrder(table);
    }
  };

  const handleCloseTableChairDialog = () => {
    setTableChairDialogOpen(false);
    setSelectedTableForChairs(null);
  };

  const handleSelectSale = (sale) => {
    setSelectedSale(sale);
    setSalesDialogOpen(false);

    // If we want to open the cart details dialog:
    setDialogOpen(true);
    setSearchTerm("");
  };

  const getStatusClass = (status) => {
    switch (status) {
      case TABLE_STATUS.AVAILABLE:
        return styles.available;
      case TABLE_STATUS.DINE_IN:
        return styles.ongoing;
      case TABLE_STATUS.NOT_PAID:
        return styles.notPaid;
      default:
        return styles.available;
    }
  };

  useEffect(() => {
    for (let i = 0; i < 15; i++) {
      navigate(ROUTES.TABLE_DASHBOARD, { replace: false });
    }

    const handlePopState = (event) => {
      event.preventDefault();
      navigate(ROUTES.TABLE_DASHBOARD, { replace: true });
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.POP_STATE, handlePopState);
    return unsubscribeFn;
  }, [navigate]);

  const handleAreaChange = (areaId) => {
    setActiveArea(areaId);
    setSearchTerm("");
    localStorage.setItem(STORAGE_KEYS.TABLE_DASHBOARD_ACTIVE_AREA, String(areaId));
  };

  const handleTableSelectionMode = (sale) => {
    setSaleToChange(sale);
    setIsSelectionMode(true);
    setNewSelectedTable(null);
  };

  const handleTableMergeMode = (sale) => {
    if (sale?.editing_sale_local_id) {
      toast.error("Can't merge editing sale");
      return;
    }
    setIsMergeMode(true);
    setSelectedSalesForMerge([sale]);
  };

  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    setIsMergeMode(false);
    setSaleToChange(null);
    setNewSelectedTable(null);
    setSelectedSalesForMerge([]);
  };

  const handleToggleMergeSale = (sale) => {
    if (sale?.editing_sale_local_id) {
      toast.error("Can't merge editing sale");
      return;
    }
    setSelectedSalesForMerge((prev) => {
      const isAlreadyIn = prev.some((s) => String(s.local_id) === String(sale.local_id));
      if (isAlreadyIn) {
        return prev.filter((s) => String(s.local_id) !== String(sale.local_id));
      } else {
        return [...prev, sale];
      }
    });
  };

  const handleWebInvoicePrint = useWebInvoicePrint({
    contentRef: kotPrintRef,
    documentTitle: `KOT-${activeSale?.bill_no || "NA"}`,
  });

  const handleSaveChangeTable = async (shouldPrintKOT = false) => {
    if (!newSelectedTable || !saleToChange) return;

    setIsTableChange(true);
    try {
      const oldTable = saleToChange?.table;
      const updatedSale = await handleChangeSaleTable(
        saleToChange?.local_id,
        newSelectedTable,
        newSelectedTable?.chair,
      );

      if (updatedSale && shouldPrintKOT) {
        const oldChairSuffix = oldTable?.chair?.name ? ` > ${oldTable.chair.name}` : "";
        const newChairSuffix = newSelectedTable?.chair?.name ? ` > ${newSelectedTable.chair.name}` : "";
        const oldTableName = `${oldTable?.area?.name || "-"} > ${oldTable?.name || "-"}${oldChairSuffix}`;
        const newTableName = `${newSelectedTable?.area?.name || "-"} > ${newSelectedTable?.name || "-"}${newChairSuffix}`;
        const tableNotes = [`Table Changed:\n${oldTableName}  -->  ${newTableName}`];

        const terminalSettings = TerminalHelper.getTerminalSettings();
        const showItems = !!terminalSettings.showItemsOnKotWhenTableChange;

        setSaleToPrint({
          ...updatedSale,
          titles: [KOT_TITLES.TABLE_CHANGED],
          notes: tableNotes,
          showItems,
        });

        await KotManager.printKot({
          sale: updatedSale,
          fallbackPrintFunction: handleWebInvoicePrint,
          titles: [KOT_TITLES.TABLE_CHANGED],
          notes: tableNotes,
          skipConsolidatedKOT: !terminalSettings.consolidatedKOT,
          showItems,
        });
      }
      handleCancelSelection();
    } catch (err) {
      console.error("Error changing table:", err);
      toast.error("Error changing table");
    } finally {
      setIsTableChange(false);
    }
  };

  const handleSaveMergeSales = async () => {
    if (selectedSalesForMerge.length < 2) {
      toast.error("Please select at least 2 sales to merge.");
      return;
    }

    // Use the table of the first selected sale as target
    const targetTable = selectedSalesForMerge[0].table;

    const success = await handleMergeSales(selectedSalesForMerge, targetTable);
    if (success) {
      toast.success("Sales merged successfully.");
      handleCancelSelection();
    } else {
      toast.error("Merge failed.");
    }
  };

  return (
    <>
      {/* Area Tabs */}
      <div className={styles.areaTabs}>
        {isLoading ? (
          <SkeletonLoading isOpen={true} count={3} height={36} xs={6} sm={4} md={2} lg={1} />
        ) : (
          sortedAreas.map((area) => (
            <Button
              key={area.id}
              className={`${styles.areaBtn} ${activeArea === area.id ? styles.activeArea : ""}`}
              onClick={() => handleAreaChange(area.id)}
            >
              {area.name}
            </Button>
          ))
        )}

        <Button
          className={`${styles.areaBtn} ${styles.allTablesBtn}  ${activeArea === ALL_TABLES ? styles.activeArea : ""}`}
          onClick={() => handleAreaChange(ALL_TABLES)}
        >
          All Tables
        </Button>
      </div>

      {/* Tables Grid */}
      <div className={styles.tablesWrapper}>
        {isLoading ? (
          <SkeletonLoading isOpen={true} count={6} height={237} xs={6} sm={4} md={2} lg={2} />
        ) : tables.length === 0 ? (
          <Grid container spacing={1.25}>
            {/* Quick action buttons */}
            <Grid item xs={6} sm={4} md={2}>
              <QuickSaleButtons />
            </Grid>
          </Grid>
        ) : activeArea === ALL_TABLES ? (
          sortedAreas.map((area) => {
            const areaTables = tables.filter((t) => t?.area?.id === area.id);
            if (areaTables.length === 0) return null;

            return (
              <div key={area.id} className={styles.areaSection}>
                <h3 className={styles.areaSectionHeader}>{area.name}</h3>
                <Grid
                  container
                  spacing={1.25}
                  className={`${area.id === lastNonEmptyAreaId ? styles.lastTablesGrid : ""}`}
                >
                  {area.id === firstNonEmptyAreaId && (
                    <Grid item xs={6} sm={4} md={2}>
                      <QuickSaleButtons />
                    </Grid>
                  )}
                  {areaTables.map((table) => {
                    const isTableSelected =
                      (isSelectionMode && newSelectedTable?.id === table.id) ||
                      (isMergeMode &&
                        table.sales?.some((s) =>
                          selectedSalesForMerge.some((m) => String(m.local_id) === String(s.local_id)),
                        ));

                    return (
                      <Grid item xs={6} sm={4} md={2} key={table.id}>
                        <TableCard
                          table={table}
                          getStatusClass={getStatusClass}
                          onClick={() => handleTableClick(table)}
                          isSelectionMode={isSelectionMode || isMergeMode}
                          isSelected={isTableSelected}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </div>
            );
          })
        ) : (
          <Grid container spacing={1.25} className={styles.tablesGrid}>
            {/* Quick action buttons */}
            <Grid item xs={6} sm={4} md={2}>
              <QuickSaleButtons />
            </Grid>
            {tables.map((table) => {
              const isTableSelected =
                (isSelectionMode && newSelectedTable?.id === table.id) ||
                (isMergeMode &&
                  table.sales?.some((s) =>
                    selectedSalesForMerge.some((m) => String(m.local_id) === String(s.local_id)),
                  ));

              return (
                <Grid item xs={6} sm={4} md={2} key={table.id}>
                  <TableCard
                    table={table}
                    getStatusClass={getStatusClass}
                    onClick={() => handleTableClick(table)}
                    isSelectionMode={isSelectionMode || isMergeMode}
                    isSelected={isTableSelected}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}
      </div>

      <TableOrdersDialog
        open={salesDialogOpen}
        onClose={handleCloseSalesDialog}
        onSelectSale={handleSelectSale}
        onNewOrder={handleNewOrderFromSalesDialog}
        table={liveTableForSales}
        filteredSales={filteredSalesForDialog}
        title={salesDialogTitle}
        selectionMode={isMergeMode ? TABLE_SELECTION_MODE.MULTI : TABLE_SELECTION_MODE.SINGLE}
        selectedSales={selectedSalesForMerge}
        onToggleSale={handleToggleMergeSale}
      />

      <TableChairDialog
        open={tableChairDialogOpen}
        onClose={handleCloseTableChairDialog}
        table={liveTableForChairs}
        activeSales={activeSalesForChairDialog}
        isSelectionMode={isSelectionMode}
        onSelectTable={handleSelectTableFromDialog}
        onSelectChair={handleSelectChairFromDialog}
        onSelectSale={handleSelectSaleFromChairDialog}
        onViewOrders={handleViewFilteredOrders}
        onViewAllOrders={handleViewAllOrdersFromChairDialog}
      />

      <CartDetailsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        saleId={selectedSale?.local_id}
        onTableSelectionMode={handleTableSelectionMode}
        onTableMergeMode={handleTableMergeMode}
        mergeLabel={MERGE_LABELS.TABLE}
      />

      {(isSelectionMode || isMergeMode) && (
        <div className={styles.selectionBar}>
          <div>
            <span className={styles.instructionText}>
              {isMergeMode ? "Select the tables/chairs you want to merge." : "Choose a table to proceed."}
            </span>
          </div>
          <div className={styles.selectionInfo}>
            {isSelectionMode && newSelectedTable && (
              <span className={styles.selectedText}>
                {newSelectedTable?.area?.name} {newSelectedTable?.name}
                {newSelectedTable?.chair?.name ? ` (${newSelectedTable.chair.name})` : ""}
              </span>
            )}
            {isMergeMode && selectedSalesForMerge.length > 0 && (
              <span className={styles.selectedText}>
                {selectedSalesForMerge
                  .map((sale) => {
                    const table = sale?.table;
                    const areaName = table?.area?.name || "-";
                    const tableName = table?.name || "-";
                    const chairName = sale?.table?.chair?.name;
                    const chairSuffix = chairName ? ` > ${chairName}` : "";
                    return `${areaName} > ${tableName}${chairSuffix}`;
                  })
                  .join(" | ")}
              </span>
            )}
          </div>
          <div className={styles.selectionActions}>
            <Button
              variant="contained"
              className={styles.cancelBtn}
              onClick={handleCancelSelection}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            {isSelectionMode ? (
              <>
                <Button
                  variant="contained"
                  className={styles.saveBtn}
                  onClick={() => handleSaveChangeTable(false)}
                  disabled={!newSelectedTable}
                  sx={{ textTransform: "none" }}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  className={styles.saveKotBtn}
                  onClick={() => handleSaveChangeTable(true)}
                  disabled={!newSelectedTable}
                  sx={{ textTransform: "none" }}
                >
                  Save & KOT
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                className={styles.saveBtn}
                onClick={handleSaveMergeSales}
                disabled={selectedSalesForMerge.length < 2 && !isTableChange}
                sx={{ textTransform: "none" }}
              >
                Save & Proceed
              </Button>
            )}
          </div>
        </div>
      )}
      <div style={{ display: "none" }}>
        <KOTInvoice ref={kotPrintRef} sale={saleToPrint} shop={selectedShop} tables={tables} />
      </div>
    </>
  );
}

export default TableDashboard;
